/**
 * Escalation Service — P-W34 Phase 3 (Practice-Web)
 *
 * Practice-side facade für care_practice_escalations.
 * Tabelle + Backend-RPCs sind aus Phase 1 (`2de491b7`):
 *  - acknowledge_escalation_v1(id) — practice marks received
 *  - resolve_escalation_v1(id, action_type, resolution_note)
 *  - care_practice_escalation_audit append-only
 *
 * RLS: SELECT erlaubt für practice_staff der entsprechenden practice_id.
 * Mutations: SECURITY DEFINER RPCs (REVOKE direct DML).
 */

import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { logger } from '@/lib/logger';
import type {
    Escalation,
    EscalationActionType,
    EscalationAuditEntry,
    EscalationStats,
    EscalationStatus,
    EscalationActorType,
} from '@/types/escalation';
import { AWAITING_STATES, IN_PROGRESS_STATES } from '@/types/escalation';

// ──────────────────────────────────────────────────────────────────────────
// Row mappers
// ──────────────────────────────────────────────────────────────────────────

function mapEscalationRow(row: Record<string, unknown>): Escalation {
    const residents = row.residents as Record<string, unknown> | null | undefined;
    const facilities = row.facilities as Record<string, unknown> | null | undefined;
    return {
        id: row.id as string,
        facilityId: row.facility_id as string,
        practiceId: row.practice_id as string,
        residentId: row.resident_id as string,
        partnershipId: row.partnership_id as string,
        originatingStaffId: row.originating_staff_id as string,
        originatingShiftType: (row.originating_shift_type as string) ?? null,
        originatingShiftDate: (row.originating_shift_date as string) ?? null,
        severity: row.severity as Escalation['severity'],
        category: row.category as Escalation['category'],
        subject: row.subject as string,
        body: row.body as string,
        state: row.state as EscalationStatus,
        sentAt: (row.sent_at as string) ?? null,
        deliveredAt: (row.delivered_at as string) ?? null,
        firstReadAt: (row.first_read_at as string) ?? null,
        acknowledgedAt: (row.acknowledged_at as string) ?? null,
        acknowledgedByPracticeStaffId: (row.acknowledged_by_practice_staff_id as string) ?? null,
        actionSetAt: (row.action_set_at as string) ?? null,
        actionType: (row.action_type as EscalationActionType) ?? null,
        actionNote: (row.action_note as string) ?? null,
        resolvedAt: (row.resolved_at as string) ?? null,
        resolvedByPracticeStaffId: (row.resolved_by_practice_staff_id as string) ?? null,
        resolutionNote: (row.resolution_note as string) ?? null,
        closedAt: (row.closed_at as string) ?? null,
        closureConfirmationNote: (row.closure_confirmation_note as string) ?? null,
        cancelledAt: (row.cancelled_at as string) ?? null,
        cancellationReason: (row.cancellation_reason as string) ?? null,
        slaDeliveredBy: (row.sla_delivered_by as string) ?? null,
        slaReadBy: (row.sla_read_by as string) ?? null,
        slaAcknowledgedBy: (row.sla_acknowledged_by as string) ?? null,
        createdAt: row.created_at as string,
        updatedAt: row.updated_at as string,
        residentFirstName: residents ? (residents.first_name as string) ?? null : null,
        residentLastName: residents ? (residents.last_name as string) ?? null : null,
        facilityName: facilities ? (facilities.name as string) ?? null : null,
    };
}

function mapAuditRow(row: Record<string, unknown>): EscalationAuditEntry {
    return {
        id: row.id as string,
        escalationId: row.escalation_id as string,
        fromState: (row.from_state as EscalationStatus) ?? null,
        toState: row.to_state as EscalationStatus,
        actorType: row.actor_type as EscalationActorType,
        transitionedAt: row.transitioned_at as string,
        metadata: (row.metadata as Record<string, unknown>) ?? null,
    };
}

// ──────────────────────────────────────────────────────────────────────────
// Queries (RLS-scoped — practice_staff sees own practice rows)
// ──────────────────────────────────────────────────────────────────────────

export async function listEscalationsForPractice(
    practiceId: string,
): Promise<ReadonlyArray<Escalation>> {
    if (!isSupabaseConfigured()) return [];

    const { data, error } = await supabase
        .from('care_practice_escalations')
        .select('*, residents(first_name, last_name), facilities:care_facilities(name)')
        .eq('practice_id', practiceId)
        .order('created_at', { ascending: false })
        .limit(200);

    if (error) {
        logger.error('listEscalationsForPractice failed', error);
        throw new Error(error.message);
    }
    return (data ?? []).map((row) => mapEscalationRow(row as Record<string, unknown>));
}

export async function getEscalation(escalationId: string): Promise<Escalation | null> {
    if (!isSupabaseConfigured()) return null;

    const { data, error } = await supabase
        .from('care_practice_escalations')
        .select('*, residents(first_name, last_name), facilities:care_facilities(name)')
        .eq('id', escalationId)
        .maybeSingle();

    if (error) {
        logger.error('getEscalation failed', error);
        throw new Error(error.message);
    }
    return data ? mapEscalationRow(data as Record<string, unknown>) : null;
}

export async function getEscalationAuditTrail(
    escalationId: string,
): Promise<ReadonlyArray<EscalationAuditEntry>> {
    if (!isSupabaseConfigured()) return [];

    const { data, error } = await supabase
        .from('care_practice_escalation_audit')
        .select('*')
        .eq('escalation_id', escalationId)
        .order('transitioned_at', { ascending: true })
        .limit(50);

    if (error) {
        logger.error('getEscalationAuditTrail failed', error);
        throw new Error(error.message);
    }
    return (data ?? []).map((row) => mapAuditRow(row as Record<string, unknown>));
}

export async function getEscalationStats(practiceId: string): Promise<EscalationStats> {
    const all = await listEscalationsForPractice(practiceId);
    return {
        total: all.length,
        awaiting: all.filter((e) => AWAITING_STATES.includes(e.state)).length,
        inProgress: all.filter((e) => IN_PROGRESS_STATES.includes(e.state)).length,
        resolved: all.filter((e) => e.state === 'resolved').length,
    };
}

// ──────────────────────────────────────────────────────────────────────────
// Mutations (via Phase 1 SECURITY DEFINER RPCs)
// ──────────────────────────────────────────────────────────────────────────

export async function acknowledgeEscalation(escalationId: string): Promise<Escalation> {
    if (!isSupabaseConfigured()) throw new Error('Supabase nicht konfiguriert');

    const { data, error } = await supabase.rpc('acknowledge_escalation_v1', {
        p_escalation_id: escalationId,
    });
    if (error) {
        logger.error('acknowledgeEscalation failed', error);
        throw new Error(error.message);
    }
    if (!data) throw new Error('acknowledge_escalation_v1 returned no row');
    return mapEscalationRow(data as Record<string, unknown>);
}

export interface ResolveEscalationInput {
    escalationId: string;
    actionType: EscalationActionType;
    resolutionNote: string;
}

export async function resolveEscalation(
    input: ResolveEscalationInput,
): Promise<Escalation> {
    if (!isSupabaseConfigured()) throw new Error('Supabase nicht konfiguriert');

    const { data, error } = await supabase.rpc('resolve_escalation_v1', {
        p_escalation_id: input.escalationId,
        p_action_type: input.actionType,
        p_resolution_note: input.resolutionNote,
    });
    if (error) {
        logger.error('resolveEscalation failed', error);
        throw new Error(error.message);
    }
    if (!data) throw new Error('resolve_escalation_v1 returned no row');
    return mapEscalationRow(data as Record<string, unknown>);
}

// ──────────────────────────────────────────────────────────────────────────
// Realtime — invalidate query when this practice's escalations change
// ──────────────────────────────────────────────────────────────────────────

let escalationChannelSeq = 0;

export interface EscalationSubscription {
    unsubscribe(): void;
}

export function subscribeToEscalations(
    practiceId: string,
    onChange: () => void,
): EscalationSubscription {
    if (!isSupabaseConfigured()) {
        return { unsubscribe() {} };
    }
    escalationChannelSeq += 1;
    const channelName = `care-escalations:practice:${practiceId}:${escalationChannelSeq}`;
    const channel = supabase
        .channel(channelName)
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'care_practice_escalations',
                filter: `practice_id=eq.${practiceId}`,
            },
            () => onChange(),
        )
        .subscribe();
    return {
        unsubscribe() {
            try {
                supabase.removeChannel(channel);
            } catch (err) {
                logger.error('subscribeToEscalations cleanup failed', err);
            }
        },
    };
}
